import { api } from '../../../shared/lib/axios';

/** Tipos de afastamento e afastamentos (change `pessoas-afastamentos`). */

export interface LeaveTypeItem {
  id: string;
  code: string;
  name: string;
  active: boolean;
  requiresCid: boolean;
  requiresDocument: boolean;
  notesRequired: boolean;
  allowsExtension: boolean;
  allowsEarlyReturn: boolean;
  updatesStatus: boolean;
  targetStatus: string | null;
  blocksAccess: boolean;
  requiresApproval: boolean;
  medical: boolean;
  integratesPayroll: boolean;
  integratesEsocial: boolean;
  integratesOccupational: boolean;
  suspendsProjects: boolean;
  suspendsApprovals: boolean;
  /** Efeito na aquisição de férias (art. 133 da CLT). */
  vacationEffect: 'nenhum' | 'suspende' | 'perde_6_meses' | 'perde_30_dias';
}

export type LeaveStatus = 'aguardando_aprovacao' | 'agendado' | 'ativo' | 'encerrado' | 'cancelado' | 'rejeitado';

export interface LeaveItem {
  id: string;
  bondId: string;
  internalRegistration: string;
  type: { id: string; code: string | null; name: string; medical: boolean };
  allowsExtension: boolean;
  allowsEarlyReturn: boolean;
  startDate: string;
  expectedEndDate: string;
  actualEndDate: string | null;
  status: LeaveStatus;
  cid: string | null;
  notes: string | null;
  documentId: string | null;
  parentLeaveId: string | null;
  rejectionReason: string | null;
  cancelledReason: string | null;
  medicalHidden: boolean;
}

export interface PendingLeave {
  id: string;
  startDate: string;
  expectedEndDate: string;
  typeName: string;
  userId: string;
  personName: string;
  internalRegistration: string;
  requestedByName: string | null;
  createdAt: string;
}

export interface TeamLeave {
  id: string;
  userId: string;
  personName: string;
  status: LeaveStatus;
  startDate: string;
  endDate: string;
  typeName: string;
}

export interface LeavePayload {
  leaveTypeId: string;
  startDate: string;
  expectedEndDate: string;
  cid?: string;
  notes?: string;
  documentId?: string;
}

function list<T>(body: unknown): T[] {
  if (Array.isArray(body)) return body as T[];
  const data = (body as { data?: unknown } | null)?.data;
  return Array.isArray(data) ? (data as T[]) : [];
}

function object<T>(body: unknown): T {
  const { version: _v, _links: _l, ...rest } = (body ?? {}) as Record<string, unknown>;
  return rest as T;
}

export async function listarTiposAfastamento(all = false): Promise<LeaveTypeItem[]> {
  const { data } = await api.get('/leave-types', { params: { all: String(all) } });
  return list<LeaveTypeItem>(data);
}

export async function salvarTipoAfastamento(id: string | null, payload: Partial<LeaveTypeItem>): Promise<LeaveTypeItem> {
  const { data } = id ? await api.patch(`/leave-types/${id}`, payload) : await api.post('/leave-types', payload);
  return object<LeaveTypeItem>(data);
}

export async function situacaoTipoAfastamento(id: string, active: boolean): Promise<LeaveTypeItem> {
  const { data } = await api.patch(`/leave-types/${id}/active`, { active });
  return object<LeaveTypeItem>(data);
}

export async function afastamentosDaPessoa(personId: string): Promise<LeaveItem[]> {
  const { data } = await api.get(`/collaborators/${personId}/leaves`);
  return list<LeaveItem>(data);
}

export async function registrarAfastamento(bondId: string, payload: LeavePayload): Promise<LeaveItem> {
  const { data } = await api.post(`/employment-bonds/${bondId}/leaves`, payload);
  return object<LeaveItem>(data);
}

export async function prorrogarAfastamento(id: string, expectedEndDate: string, documentId?: string): Promise<LeaveItem> {
  const { data } = await api.post(`/leaves/${id}/extend`, { expectedEndDate, documentId });
  return object<LeaveItem>(data);
}

export async function retornoAntecipado(id: string, endDate: string): Promise<LeaveItem> {
  const { data } = await api.post(`/leaves/${id}/early-return`, { endDate });
  return object<LeaveItem>(data);
}

export async function cancelarAfastamento(id: string, justification: string): Promise<LeaveItem> {
  const { data } = await api.post(`/leaves/${id}/cancel`, { justification });
  return object<LeaveItem>(data);
}

export async function afastamentosPendentes(): Promise<PendingLeave[]> {
  const { data } = await api.get('/leaves/pending-approval');
  return list<PendingLeave>(data);
}

export async function aprovarAfastamento(id: string): Promise<LeaveItem> {
  const { data } = await api.post(`/leaves/${id}/approve`);
  return object<LeaveItem>(data);
}

export async function rejeitarAfastamento(id: string, reason: string): Promise<LeaveItem> {
  const { data } = await api.post(`/leaves/${id}/reject`, { reason });
  return object<LeaveItem>(data);
}

export async function afastamentosDaEquipe(): Promise<TeamLeave[]> {
  const { data } = await api.get('/me/team/leaves');
  return list<TeamLeave>(data);
}
